import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';

import * as Sentry from "@sentry/browser";

declare var Penmate;

// Keep a CSS variable in sync with the device's bottom inset so footers don't get obscured.
// Prefer native-provided SAFE_AREA_INSETS; otherwise fall back to env(safe-area-inset-bottom).
const setSafeAreaBottom = (px: number) => {
  const inset = Math.max(0, Number(px) || 0);
  const root = document.documentElement;
  root.style.setProperty('--app-safe-bottom', `${inset}px`);
  root.style.setProperty('--ion-safe-area-bottom', `${inset}px`);
};

const hasNativeSafeAreaInset = () =>
  typeof window !== 'undefined' && Boolean((window as any).__pmNativeSafeAreaBottom);

const getEnvSafeAreaBottom = () => {
  try {
    const root = document.documentElement;
    const computed = getComputedStyle(root);
    const raw =
      computed.getPropertyValue('--ion-safe-area-bottom') ||
      computed.getPropertyValue('--app-safe-bottom');
    const parsed = Number.parseFloat(raw || '');
    return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
  } catch {
    return 0;
  }
};

const applySafeAreaInsetFallback = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (hasNativeSafeAreaInset()) return;

  const envInset = getEnvSafeAreaBottom();
  const vv = (window as any).visualViewport;
  if (vv) {
    const vvInset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
    const keyboardLikelyOpen = vvInset > 80; // avoid using keyboard height as inset
    const viewportShrunk = vv.height <= window.innerHeight + 1;
    if (!keyboardLikelyOpen && viewportShrunk) {
      setSafeAreaBottom(Math.max(envInset, vvInset));
      return;
    }
  }

  setSafeAreaBottom(envInset);
};

if (typeof window !== 'undefined') {
  applySafeAreaInsetFallback();
  const vv: any = (window as any).visualViewport;
  if (vv && typeof vv.addEventListener === 'function') {
    vv.addEventListener('resize', applySafeAreaInsetFallback);
  }
  window.addEventListener('resize', applySafeAreaInsetFallback);
}

const getGlitchTipDsn = (): string => {
  const env = typeof Penmate !== 'undefined' && Penmate.env ? Penmate.env : null;
  const dsn = env && typeof env.glitchTipDsn === 'string' ? env.glitchTipDsn : '';
  return dsn.trim();
};

const glitchTipDsn = getGlitchTipDsn();
if (glitchTipDsn) {
  Sentry.init({
    dsn: glitchTipDsn,
    environment: typeof Penmate !== 'undefined' && Penmate.env ? Penmate.env.name : undefined,
    // Keep it lightweight; we're focused on error reporting over perf/replay.
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
  });
}

if (environment.production) {
  enableProdMode();
}

platformBrowserDynamic()
  .bootstrapModule(AppModule)
  .catch(err => console.log(err));
